/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Label, Dropdown, Segment } from 'semantic-ui-react';
import { Search } from 'semantic-ui-react';
import { get } from '../../../../utils/lodash';
import {
  CustomTable,
  EmptyContainer,
  CustomSegment,
} from '../../../../components';
import {
  searchAllActivities,
  loginAsUser,
  getUserProfile,
} from '../../../../store/actions';
import { APP_NAME } from '../../../../utils/constant';
import { userAuthMgr } from '../../../../utils/auth';

import './searchActivities.scss';

const SearchActivities = ({
  t,
  searchAllActivities,
  loginAsUser,
  getUserProfile,
  user,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState('all'); // all, ongoing, completed
  const [actType, setActType] = useState('all'); // all, single, grant, festival, multi-activity
  const [group, setGroup] = useState('all'); // all groups
  const [attributeName, setAttributeName] = useState('name');
  const [order] = useState('asc');
  const [, setDoneLoginAs] = useState(false);

  const navigate = useNavigate();

  // Debounce the search term to avoid too many API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800); // Increased debounce delay to 800ms

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const performSearch = async (page = 1) => {
    try {
      setLoading(true);

      const result = await searchAllActivities(
        debouncedSearchTerm,
        page,
        20,
        filter,
        actType,
        group,
        attributeName,
        order
      );

      console.log('Search result:', result);
      setActivities(result?.rows || []);
      setTotalPages(result?.totalPages || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Search error:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${t('common.activities')} | ${APP_NAME}`;
  }, []);

  useEffect(() => {
    // Perform search when debounced search term changes
    if (debouncedSearchTerm.trim() !== '') {
      performSearch(1);
    } else {
      // If search is empty, still fetch all activities
      performSearch(1);
    }
  }, [debouncedSearchTerm, filter, actType, group]);

  const handleSearchChange = (e, { value }) => {
    setSearchTerm(value);
    // Auto-detect: if numeric, search by ID
    if (/^\d+$/.test(value.trim())) {
      setAttributeName('id');
    } else {
      setAttributeName('name');
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setDebouncedSearchTerm(searchTerm);
  };

  const getStatusLabel = (statusId) => {
    switch (statusId) {
      case 1:
        return { text: t('activity_status.labels.active'), class: 'warning' };
      case 2:
        return { text: t('activity_status.labels.greenlit'), class: 'success' };
      case 3:
        return {
          text: t('activity_status.labels.completed'),
          class: 'completed',
        };
      case 4:
        return {
          text: t('activity_status.labels.cancelled'),
          class: 'completed',
        };
      default:
        return { text: t('activity_status.labels.draft'), class: 'warning' };
    }
  };

  const statusOptions = [
    {
      key: 'All Activities',
      text: t('activity_status.all_activities'),
      value: 'all',
    },
    {
      key: 'Ongoing',
      text: t('activity_status.ongoing_activities'),
      value: 'ongoing',
    },
    {
      key: 'Completed',
      text: t('activity_status.completed_activities'),
      value: 'completed',
    },
  ];

  const actTypeOptions = [
    {
      key: 'All',
      text: t('activity_type.all_types'),
      value: 'all',
    },
    {
      key: 'Single',
      text: t('activity_type.single'),
      value: 'single',
    },
    {
      key: 'Grant',
      text: t('activity_type.grant'),
      value: 'grant',
    },
    {
      key: 'Festival',
      text: t('activity_type.festival'),
      value: 'festival',
    },
    {
      key: 'Multi Activity',
      text: t('activity_type.multi_activity'),
      value: 'multi-activity',
    },
  ];

  const groupOptions = [
    {
      key: 'all',
      text: t('activity.home.no_groups'),
      value: 'all',
    },
    // Add more groups as needed
  ];

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleActTypeChange = (newActType) => {
    setActType(newActType);
  };

  const handleGroupChange = (newGroup) => {
    setGroup(newGroup);
  };

  const handlePageChange = (page) => {
    performSearch(page);
  };

  const doLoginAs = async (activityId, userId) => {
    try {
      await loginAsUser(userId);
      await userAuthMgr.authenticate();
      await getUserProfile();
      setDoneLoginAs(true);
      navigate(`/activities/${activityId}`);
    } catch (error) {
      console.error('LOGIN AS ~ error', error);
    }
  };

  const columns = [
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>ID</strong>
        </div>
      ),
      render: (data) => {
        return <div className="activity-id">#{get(data, 'id', 'N/A')}</div>;
      },
      width: 2,
    },
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>{t('common.activity_name')}</strong>
        </div>
      ),
      render: (data) => {
        return (
          <Link to={`/admin/activities/${data.id}`}>
            {get(data, 'name', 'N/A')}
          </Link>
        );
      },
      width: 5,
    },
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>{t('common.owner')}</strong>
        </div>
      ),
      render: (data) => {
        return <div>{get(data, 'owner.name', '-')}</div>;
      },
      width: 2,
    },
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>{t('common.organisation')}</strong>
        </div>
      ),
      render: (data) => {
        return <div>{get(data, 'owner.organisation.name', 'N/A')}</div>;
      },
      width: 3,
    },
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>{t('common.status')}</strong>
        </div>
      ),
      render: (data) => {
        const status = getStatusLabel(get(data, 'statusId', 1));
        return (
          <Label className={`status-label ${status.class}`}>
            {status.text}
          </Label>
        );
      },
      width: 2,
    },
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong></strong>
        </div>
      ),
      render: (data) => {
        return (
          <div className="owned">
            <Dropdown
              icon="ellipsis vertical"
              direction="left"
              inline
              selectOnBlur={false}
            >
              <Dropdown.Menu>
                <Dropdown.Item
                  text={t('common.view_activity')}
                  className="dropdown-customItem"
                >
                  <Link to={`/admin/activities/${data.id}`}>
                    {t('common.view_activity')}
                  </Link>
                </Dropdown.Item>

                {data.owner && !data.owner.disabled && (
                  <Dropdown.Item
                    text={`${t('common.login_as')}...`}
                    className="dropdown-customItem"
                  >
                    <span
                      className="login-as"
                      onClick={(e) => doLoginAs(data.id, data.owner.id)}
                    >
                      {t('admin.users.login_as', {
                        name: data.owner.name,
                        id: data.owner.id,
                      })}
                    </span>
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );

        /*********
        return (
          <div className="action-btns">
            <Popup
              trigger={
                <Button
                  icon
                  basic
                  size="small"
                  as={Link}
                  to={`/admin/activities/${data.id}`}
                >
                  <Icon name="eye" />
                </Button>
              }
              content="View Activity"
              position="top center"
            />
            <Popup
              trigger={
                <Button
                  icon
                  basic
                  size="small"
                  as={Link}
                  to={`/admin/activities/${data.id}/goal`}
                >
                  <Icon name="edit" />
                </Button>
              }
              content="Edit Activity"
              position="top center"
            />
          </div>
        );
        ****************/
      },
      width: 2,
    },
  ];

  const renderTable = () => {
    return (
      <CustomTable
        customClass="super-admin-table home-activity"
        columns={columns}
        data={activities}
        emptyMessage={
          searchTerm.trim() || filter !== 'all' || actType !== 'all'
            ? t('activity.search.no_activities_found_matching_filters')
            : t('activity.search.no_activities_found')
        }
        keyExtractor={(item) => item.id}
        loading={loading}
        pagination
        handlePaginationChange={handlePageChange}
        page={currentPage}
        noOfPages={totalPages}
        header
      />
    );
  };

  return (
    <div className="admin-activities-container">
      <CustomSegment
        title={t('activity_status.all_activities')}
        subChild={
          <div className="sub-child-segment navbar">
            <form
              onSubmit={handleSearchSubmit}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Search
                className="search-bar-custom"
                placeholder={t('activity.search.search_by_activity_name_or_id')}
                showNoResults={false}
                onSearchChange={handleSearchChange}
                value={searchTerm}
                loading={loading}
              />
              <Button
                type="submit"
                className="Primary-Button"
                loading={loading}
                style={{ marginLeft: '10px' }}
              >
                {t('actions.search')}
              </Button>
            </form>
            <div className="activities-filter-container manage-activity-drop">
              <span className="activities-filter">
                <Dropdown
                  direction="left"
                  inline
                  selectOnBlur={false}
                  options={groupOptions}
                  value={group}
                  onChange={(e, { value }) => handleGroupChange(value)}
                />
              </span>
              <span className="activities-filter">
                <Dropdown
                  direction="left"
                  inline
                  selectOnBlur={false}
                  options={actTypeOptions}
                  value={actType}
                  onChange={(e, { value }) => handleActTypeChange(value)}
                />
              </span>
              <span className="activities-filter">
                <Dropdown
                  direction="left"
                  inline
                  selectOnBlur={false}
                  options={statusOptions}
                  value={filter}
                  onChange={(e, { value }) => handleFilterChange(value)}
                />
              </span>
            </div>
          </div>
        }
        extraMargin
      >
        <Segment
          className="content-segment content-segment-bg"
          loading={loading}
          style={{ marginTop: '20px' }}
        >
          {activities && activities.length ? (
            renderTable()
          ) : (
            <EmptyContainer msg={`${t('common.no_activities')}…`} />
          )}
        </Segment>
      </CustomSegment>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.authentication.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      searchAllActivities,
      loginAsUser,
      getUserProfile,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SearchActivities));
